/**
 * WS Logger
 *
 * Controlled via localStorage:
 *   localStorage.setItem('ws_log', '1')   // enable
 *   localStorage.removeItem('ws_log')      // disable
 *
 * Or via console helpers (auto-attached to window in non-production):
 *   wsLog.enable()
 *   wsLog.disable()
 *   wsLog.status()
 */

const STORAGE_KEY = "ws_log";
const PREFIX = "%c[WS]";
const STYLE_DEFAULT  = "color:#7C3AED;font-weight:700";
const STYLE_IN       = "color:#059669;font-weight:700";
const STYLE_OUT      = "color:#2563EB;font-weight:700";
const STYLE_STATUS   = "color:#D97706;font-weight:700";
const STYLE_ERROR    = "color:#DC2626;font-weight:700";
const STYLE_RESPONSE = "color:#0891B2;font-weight:700";

function isEnabled() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

// ── Public API ──────────────────────────────────────────────────────────────

export const wsLogger = {
  /** Connection status changes (connecting, connected, disconnected…) */
  status(status, extra) {
    if (!isEnabled()) return;
    const msg = extra ? `${status} ${extra}` : status;
    console.log(PREFIX, STYLE_STATUS, `status → ${msg}`);
  },

  /** Raw message received from server (before routing) */
  recv(msg) {
    if (!isEnabled()) return;
    const label = msg?.type === "response"
      ? `response ← [${msg.request_id}] status=${msg.status}`
      : `push ← ${msg?.type ?? "unknown"}`;
    console.groupCollapsed(PREFIX + " " + label, STYLE_IN);
    console.log("payload:", msg);
    console.groupEnd();
  },

  /** Outgoing sendRequest call */
  send(action, requestId, data) {
    if (!isEnabled()) return;
    console.groupCollapsed(`${PREFIX} send → ${action} [${requestId}]`, STYLE_OUT);
    console.log("data:", data);
    console.groupEnd();
  },

  /** sendRequest resolved (success response) */
  response(action, requestId, msg) {
    if (!isEnabled()) return;
    console.groupCollapsed(`${PREFIX} response ✓ ${action} [${requestId}] status=${msg?.status}`, STYLE_RESPONSE);
    console.log("data:", msg?.data);
    console.groupEnd();
  },

  /** sendRequest rejected (error response or timeout) */
  error(action, requestId, err) {
    if (!isEnabled()) return;
    const label = err?.status
      ? `response ✗ ${action} [${requestId}] status=${err.status}`
      : `error ${action} [${requestId}]`;
    console.groupCollapsed(PREFIX + " " + label, STYLE_ERROR);
    console.log("error:", err);
    console.groupEnd();
  },

  /** Push event dispatched to Redux */
  push(type, results) {
    if (!isEnabled()) return;
    console.groupCollapsed(`${PREFIX} push event → ${type} (${results?.length ?? "?"} items)`, STYLE_DEFAULT);
    console.log("results:", results);
    console.groupEnd();
  },

  enable()  { localStorage.setItem(STORAGE_KEY, "1");  console.log("[WS] logging enabled");  },
  disable() { localStorage.removeItem(STORAGE_KEY);    console.log("[WS] logging disabled"); },
  status_check() { console.log("[WS] logging:", isEnabled() ? "ON" : "OFF"); },
};

// Attach helpers to window in non-production for easy console access
if (process.env.REACT_APP_NODE_ENV !== "production") {
  window.wsLog = wsLogger;
}
